import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { UNSAFE_NavigationContext, type Navigator } from 'react-router-dom';

const GUARD_KEY = '__unsavedGuard';

type To = Parameters<Navigator['push']>[0];

const pathnameOf = (to: To) => (typeof to === 'string' ? new URL(to, window.location.href).pathname : to.pathname ?? window.location.pathname);

const onGuardEntry = () => Boolean((window.history.state as Record<string, unknown> | null)?.[GUARD_KEY]);

const pushGuardEntry = () => window.history.pushState({ ...(window.history.state ?? {}), [GUARD_KEY]: true }, '');

const preventUnload = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  event.returnValue = '';
};

type Flag = { current: boolean };

interface GuardState {
  onEntry: Flag;
  bypass: Flag;
  dirtyRef: Flag;
  ask: (next: { proceed: () => void; cancel?: () => void }) => void;
}

// Wraps the router's push, replace and go so leaving while dirty asks first; returns the undo
function wrapNavigator(nav: Navigator, { onEntry, bypass, dirtyRef, ask }: GuardState) {
  const { push, replace, go } = nav;
  const leaveTo = (to: To, state: unknown, opts: Parameters<Navigator['push']>[2]) => {
    // Leaving from the copy replaces it, so history keeps one entry for this page
    if (onEntry.current) replace.call(nav, to, state, opts);
    else push.call(nav, to, state, opts);
    onEntry.current = false;
  };
  nav.push = (to, state, opts) => {
    if (pathnameOf(to) === window.location.pathname) {
      push.call(nav, to, state, opts);
      onEntry.current = false;
      return;
    }
    if (bypass.current || !dirtyRef.current) {
      leaveTo(to, state, opts);
      return;
    }
    ask({ proceed: () => leaveTo(to, state, opts) });
  };
  nav.replace = (to, state, opts) => {
    const leaves = pathnameOf(to) !== window.location.pathname;
    const run = () => {
      const wasOnEntry = onEntry.current;
      replace.call(nav, to, state, opts);
      onEntry.current = false;
      if (wasOnEntry && !leaves) {
        window.history.replaceState({ ...(window.history.state ?? {}), [GUARD_KEY]: true }, '');
        onEntry.current = true;
      }
    };
    if (bypass.current || !dirtyRef.current || !leaves) run();
    else ask({ proceed: run });
  };
  nav.go = (delta) => {
    const run = () => {
      const skipCopy = onEntry.current && delta < 0;
      onEntry.current = false;
      go.call(nav, skipCopy ? delta - 1 : delta);
    };
    if (bypass.current || !dirtyRef.current || delta === 0) run();
    else ask({ proceed: run });
  };
  return () => {
    nav.push = push;
    nav.replace = replace;
    nav.go = go;
  };
}

// BrowserRouter has no useBlocker: the navigator is wrapped, and Back first lands on a marked copy of this entry
export function useUnsavedChangesGuard(dirty: boolean) {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const [asking, setAsking] = useState(false);
  const pending = useRef<{ proceed: () => void; cancel?: () => void } | null>(null);
  const dirtyRef = useRef(dirty);
  const bypass = useRef(false);
  const onEntry = useRef(onGuardEntry());
  const leavingByHistory = useRef(false);

  useLayoutEffect(() => {
    dirtyRef.current = dirty;
  });

  const ask = useCallback((next: { proceed: () => void; cancel?: () => void }) => {
    pending.current = next;
    setAsking(true);
  }, []);

  useEffect(() => wrapNavigator(navigator as Navigator, { onEntry, bypass, dirtyRef, ask }), [navigator, ask]);

  useEffect(() => {
    if (!dirty || onEntry.current) return;
    pushGuardEntry();
    onEntry.current = true;
  }, [dirty]);

  useEffect(() => {
    const onPop = () => {
      if (onGuardEntry()) {
        onEntry.current = true;
        return;
      }
      if (leavingByHistory.current) {
        leavingByHistory.current = false;
        return;
      }
      if (!onEntry.current) return;
      onEntry.current = false;
      if (bypass.current) return;
      if (!dirtyRef.current) {
        window.history.back();
        return;
      }
      // The marked copy goes back on top at once, so another Back while the question is open asks again instead of leaving
      pushGuardEntry();
      onEntry.current = true;
      ask({
        proceed: () => {
          leavingByHistory.current = true;
          onEntry.current = false;
          window.history.go(-2);
        },
      });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [ask]);

  useEffect(() => {
    if (!dirty) return;
    window.addEventListener('beforeunload', preventUnload);
    return () => window.removeEventListener('beforeunload', preventUnload);
  }, [dirty]);

  useEffect(() => {
    if (!dirty) return;
    // Router links have already called preventDefault and gone through the navigator
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor || (anchor.target && anchor.target !== '_self') || anchor.hasAttribute('download') || anchor.origin !== window.location.origin) return;
      if (anchor.pathname === window.location.pathname) return;
      event.preventDefault();
      (navigator as Navigator).push(anchor.pathname + anchor.search + anchor.hash);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [dirty, navigator]);

  const run = useCallback((navigation: () => void) => {
    window.removeEventListener('beforeunload', preventUnload);
    bypass.current = true;
    try {
      navigation();
    } finally {
      bypass.current = false;
    }
  }, []);

  const proceed = useCallback(() => {
    const next = pending.current;
    if (!next) return;
    pending.current = null;
    setAsking(false);
    run(next.proceed);
  }, [run]);

  const cancel = useCallback(() => {
    const next = pending.current;
    if (!next) return;
    pending.current = null;
    setAsking(false);
    next.cancel?.();
  }, []);

  return { asking, proceed, cancel, leave: run };
}
